import { Form, FormProps } from 'antd';
import { SelectProps } from 'antd/lib';
import { useMemo } from 'react';
import { useOutletContext, useSearchParams } from 'react-router';

import useFormDraftInit from '@/hooks/useFormDraftInit';
import useFormFieldsDependences from '@/hooks/useFormFieldsDependences';
import { useSelectScrollSearch } from '@/hooks/useSelectScrollSearch';
import { useSelectPropsWithInitial } from '@/hooks/useSelectWithInitialValue';
import { Currency } from '@/model/General/Currency';
import { SERVICE_ORDER_STATUS } from '@/model/ServiceOrder/ServiceOrder';
import { Ticket } from '@/model/Ticket/Ticket';
import { RESOURCES } from '@/Resources';
import { formatTicketLabel } from '@/utils/formHelpers';

import useCompanyContacts from './useCompanyContacts';
import usePaymentMethods from './usePaymentMethods';
import useTechnicianPricing from './useTechnicianPricing';
import useTicketMachines from './useTicketMachines';

const useServiceOrderForm = (formProps: FormProps) => {
  const [searchParams] = useSearchParams();
  const company = searchParams.get('company') ? Number(searchParams.get('company')) : undefined;
  const ticket = searchParams.get('ticket') ? Number(searchParams.get('ticket')) : undefined;
  const flattenInitialValues = useMemo(
    () =>
      formProps.initialValues
        ? {
            ...formProps.initialValues,
            tickets: formProps.initialValues?.tickets.map((ticket: Ticket) => ticket.id) ?? [],
            contact_data: formProps.initialValues?.contact_data
              ? parseInt(formProps.initialValues?.contact_data)
              : undefined,
            currency: formProps.initialValues?.currency?.id,
            hours_covered: formProps.initialValues?.hours_covered ?? 0,
            kilometres_covered: formProps.initialValues?.kilometres_covered ?? 0,
            moviles: formProps.initialValues?.moviles.map((m: any) => m.id) ?? [],
          }
        : {
            tickets: ticket ? [ticket] : [],
            status: SERVICE_ORDER_STATUS.PENDIENTE_DE_COORDINACION,
            company: company,
            hours_covered: 0,
            kilometres_covered: 0,
          },
    [company, formProps.initialValues, ticket],
  );
  const { formInitialValues, initialValuesSetted, clearDraft } = useFormDraftInit({
    form: formProps.form,
    draftEnabled: true,
    initialValues: flattenInitialValues,
  });

  useFormFieldsDependences(formProps.form, formInitialValues ?? {}, initialValuesSetted, [
    {
      watchField: 'company',
      resetFields: ['tickets', 'movil'],
    },
  ]);

  const selectedCompany = Form.useWatch('company', formProps.form);
  // Selectores base desde el outlet
  const { companySelectProps, technicianSelectProps } = useOutletContext<{
    companySelectProps: SelectProps;
    technicianSelectProps: SelectProps;
  }>();
  const { selectProps: ticketsSelectProps } = useSelectScrollSearch(
    {
      resource: RESOURCES.TICKETS,
      optionLabel: formatTicketLabel,
      optionValue: (item: any) => item.id,
      queryOptions: {
        enabled: !!selectedCompany,
      },
      filters: [
        {
          value: selectedCompany,
          operator: 'eq' as const,
          field: 'empresa',
        },
      ],
    },
    { saveItem: true },
  );
  const { selectProps: currencySelectProps } = useSelectScrollSearch(
    {
      resource: 'currencies',
      optionLabel: (item: Currency) => item.nombre + ' (' + item.abreviacion + ')',
      pagination: {
        mode: 'off',
        pageSize: 3000,
      },
    },
    { saveItem: true },
  );
  // Selectores con valores iniciales precargados
  const companySelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: companySelectProps,
    resource: 'empresas',
    initialValue:
      formInitialValues?.company != formInitialValues?.shipment_payment_company
        ? [formInitialValues?.company, formInitialValues?.shipment_payment_company]
        : formInitialValues?.company,
    labelKey: 'full_name',
    valueKey: 'id',
  });
  const technicianSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: technicianSelectProps,
    resource: 'technicians',
    initialValue: formInitialValues?.technician,
    labelKey: 'nombre',
    valueKey: 'id',
  });
  const ticketsSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: ticketsSelectProps,
    resource: RESOURCES.TICKETS,
    initialValue: formInitialValues?.tickets,
    labelKey: 'title',
    valueKey: 'id',
  });
  // Hooks de dominio extraídos
  const { companyContactsSelectProps, companyContactRules } = useCompanyContacts(
    formProps.form,
    selectedCompany,
  );
  const { ticketMachineOptions, ticketMachineOptionsLoading } = useTicketMachines(
    formProps.form,
    ticketsSelectPropsWithInitials,
  );
  const { modifiedTechnicianSelectProps, assignTechnicianPrice, selectedTechnician } =
    useTechnicianPricing(
      formProps.form,
      technicianSelectPropsWithInitials,
      formProps.initialValues?.technician ?? null,
    );
  const {
    technicianPaymentMethodsSelectProps,
    createPaymentMethod,
    clientPaymentMethodsSelectProps,
    createClientPaymentMethod,
  } = usePaymentMethods(formProps.form, selectedTechnician, selectedCompany);
  const createdAutomatically = useMemo(
    () => formInitialValues?.created_automatically,
    [formInitialValues],
  );

  return {
    formInitialValues,
    technicianSelectProps: modifiedTechnicianSelectProps,
    companySelectProps: companySelectPropsWithInitials,
    ticketsSelectProps: ticketsSelectPropsWithInitials,
    currencySelectProps,
    ticketMachineOptions,
    ticketMachineOptionsLoading,
    companyContactsSelectProps,
    companyContactRules,
    assignTechnicianPrice,
    clearDraft,
    createdAutomatically,
    technicianPaymentMethodsSelectProps,
    createPaymentMethod,
    clientPaymentMethodsSelectProps,
    createClientPaymentMethod,
  };
};

export default useServiceOrderForm;
